/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Form, Field } from 'react-jeff';
import { addDays, addMonths, getDay, parseISO, eachWeekendOfInterval, isValid } from 'date-fns';

import {
  NullableDate,
  TechLookupTechType,
  TechLookupProductType,
  TechLookupOrderType,
  TechnicianSlot,
  useTechnicianAvailableSlots,
  useSplitIO,
  SPLITIO_KEY,
  useJSONConfig,
  useAccountDetails,
  AddressService,
  JSONConfigService,
  TechnicianAvailabilityService,
  InstallationTimeCodeKeys,
  UserGroupType,
  DartMigrationCallCode,
  InstallationTimeCodes,
  New_InstallationTimeCodes,
  T_Customer,
  T_Profile,
  sKyTvInstallationYesNoOptions,
  telPhoneNumber,
  capitalize,
  firstLetterCapitalize,
  OrderServiceAddress,
  buildGetServiceAddress,
} from '@sky-tv-group/shared';
import { Loader } from '../loader';
import { ValidationDatePicker } from '../validation/dateValidated';
import { SelectElement } from '../validation/selectValidated';
import { RadioButtonGroup } from '../radioButtonGroup';
import { ValidatedTextArea, ValidatedTextInput } from '../validation/inputValidated';
import { ValidatedPhoneNumber } from '../validation/phonenumberValidated';
import { ValidatedAddressSuggest } from '../validation/addressValidated';
import { NullableAddressLookup } from '@sky-tv-group/shared/src/types';
interface IInstallationDetailsProps {
  titleText: string;
  currentAddress?: string;
  message?: string;
  formValidation: Form<string>;
  mainContactAvailabilityField?: Field<string, string>;
  alternateNameField?: Field<string, string>;
  alternateAreaCodeField?: Field<string, string>;
  alternatePhoneNumberField?: Field<string, string>;
  dogsField?: Field<string, string>;
  techNotesField?: Field<string, string>;
  installationAddressField?: Field<NullableAddressLookup, string>;
  areaCodeField?: Field<string, string>;
  phoneNumberField?: Field<string, string>;
  installationDateField: Field<NullableDate, string>;
  installationTimeField: Field<string, string>;
  setInstallationOptionsAvailability: (available: boolean) => void;
  addressService: AddressService;
  configService: JSONConfigService;
  technicianLookupService: TechnicianAvailabilityService;
  customer?: T_Customer;
  profile?: T_Profile;
  isCorrectAddress?: boolean;
  handleCallback?: (data?: any) => void;
}

const isUserType = (userGroupTypes: UserGroupType[], groupType: UserGroupType) => {
  return userGroupTypes.some(ugt => ugt === groupType);
};

const InstallationDetails: React.FunctionComponent<IInstallationDetailsProps> = ({
  titleText,
  currentAddress,
  message,
  formValidation,
  mainContactAvailabilityField,
  alternateNameField,
  alternateAreaCodeField,
  alternatePhoneNumberField,
  dogsField,
  techNotesField,
  areaCodeField,
  phoneNumberField,
  installationAddressField,
  installationDateField,
  installationTimeField,
  setInstallationOptionsAvailability,
  addressService,
  configService,
  technicianLookupService,
  customer,
  profile,
  isCorrectAddress,
  handleCallback,
}) => {
  const [shouldUseLookup] = useSplitIO(SPLITIO_KEY.SKYWEB_TECHNICIAN_LOOKUP);
  const { installationDetails, propertyDetail, updateInstallationDate, updateInstallationTime } = useAccountDetails(
    addressService
  );
  const userGroupTypes = (profile?.groups ?? []) as UserGroupType[];
  const isDartMigration = isUserType(userGroupTypes, UserGroupType.SKY_VTV_CUSTOMER);

  const {
    loadingTechnicianAvailableDates,
    errorTechnicianAvailableDates,
    getTechnicianDates,
    getTechnicianTimeSlots,
    allTimeslots,
    allTimeslotsShouldUseLookUp,
    technicianSlots,
  } = useTechnicianAvailableSlots(
    technicianLookupService,
    TechLookupTechType.SKY,
    TechLookupProductType.CABLE,
    TechLookupOrderType.INSTALL
  );
  const config = useJSONConfig(configService);

  //Technician Lookup values
  let [availableDates, setAvailableDates] = useState<Date[] | undefined>(undefined);
  let [excludeDates, setExcludeDates] = useState<Date[] | undefined>(undefined);
  let [availableTimeSlots, setAvailableTimeSlots] = useState<TechnicianSlot[]>([]);
  const [manipulatedSlots, SetManipulatedSlots] = useState<TechnicianSlot[]>([]);
  const [serviceAddress, setServiceAddress] = useState<OrderServiceAddress | null>(null);
  let { tuiid, street, suburb, city, postCode } = serviceAddress ?? {};
  const tui = customer
    ? installationAddressField?.value?.id
      ? tuiid
      : customer?.tuiAddressCode
    : propertyDetail?.address?.tui;
  let address = installationAddressField?.value?.id ? installationAddressField.value : propertyDetail.address;
  let defaultAddress = customer?.serviceAddress.primaryAddress;

  useEffect(() => {
    if (serviceAddress) return;
    street = `${defaultAddress?.houseNumber!} ${defaultAddress?.streetName!}`;
    suburb = defaultAddress?.suburb!;
    city = defaultAddress?.city!;
    postCode = defaultAddress?.postCode!;
  }, [customer, serviceAddress]);

  useEffect(() => {
    const fetchAddress = async () => {
      const result = await buildGetServiceAddress(addressService, address);
      setServiceAddress(result);
    };
    if (address?.id) fetchAddress();
  }, [address]);

  // update fields to session storage state when changed
  useEffect(() => {
    // setShowTimeSlots(true);
    if (installationDetails.installationDate === installationDateField.value) {
      installationTimeField.setValue(installationDetails.installationTime);
    } else {
      installationTimeField.setValue('');
    }
    updateInstallationDate(installationDateField.value);
    // Check for Available Time slots for the selected date
    if (installationDateField.value) {
      let slots;

      if (shouldUseLookup && !errorTechnicianAvailableDates) {
        slots = getTechnicianTimeSlots(installationDateField.value);

        // if (slots.find(ts => ts.code === InstallationTimeCodeKeys.ALLDAY)) {
        //   setShowTimeSlots(false);
        //   installationTimeField.setValue(InstallationTimeCodeKeys.ALLDAY);
        // }
      } else if (shouldUseLookup) {
        slots = allTimeslotsShouldUseLookUp;
      } else {
        slots = allTimeslots;
      }

      setAvailableTimeSlots(slots);
    }
  }, [installationDateField.value, technicianSlots, errorTechnicianAvailableDates]);

  //update the installation date to null if the technician slots gets updated
  useEffect(() => {
    if (installationDateField.value) {
      installationDateField.setValue(null);
    }
  }, [technicianSlots, errorTechnicianAvailableDates]);

  useEffect(() => {
    handleCallback?.(technicianSlots);
  }, [technicianSlots]);

  useEffect(() => {
    updateInstallationTime(installationTimeField.value);
  }, [installationTimeField.value]);

  useEffect(() => {
    setInstallationOptionsAvailability(!errorTechnicianAvailableDates);
  }, [errorTechnicianAvailableDates]);

  // initialize values from session storage
  useEffect(() => {
    if (!shouldUseLookup) {
      if (isValid(installationDetails.installationDate)) {
        installationDateField.setValue(installationDetails.installationDate);
      }
      if (installationDetails.installationTime) {
        installationTimeField.setValue(installationDetails.installationTime);
      }
    }
  }, []);

  // Manipulate the time slots to display on the web
  useEffect(() => {
    if (availableTimeSlots) {
      let filteredTimeSlots = availableTimeSlots.filter(
        (slot: any) => slot.code !== InstallationTimeCodeKeys.AM1 && slot.code !== InstallationTimeCodeKeys.PM2
      );
      const slotData = filteredTimeSlots.map(slot => {
        if (slot.displayName === InstallationTimeCodes.AM2 || slot.code === InstallationTimeCodeKeys.AM2) {
          return { ...slot, displayName: New_InstallationTimeCodes.AM2 };
        } else if (slot.displayName === InstallationTimeCodes.PM1 || slot.code === InstallationTimeCodeKeys.PM1) {
          return { ...slot, displayName: New_InstallationTimeCodes.PM1 };
        } else if (slot.displayName === InstallationTimeCodes.ALLDAY || slot.code === InstallationTimeCodeKeys.ALLDAY) {
          return { ...slot, displayName: New_InstallationTimeCodes.ALLDAY };
        }
        return slot;
      });
      SetManipulatedSlots(slotData);
    }
  }, [installationDateField.value, availableTimeSlots]);

  const dayAfterTomorrowDate: Date = addDays(new Date(), 2);
  const maxInstallationDate: Date = addMonths(dayAfterTomorrowDate, 3);
  // Get Technician Available Dates based on the selected Address
  useEffect(() => {
    const getDates = async () => {
      if (!tui) {
        return;
      }
      let tuiUnavailable = tui === '0';
      installationDateField.value = null;
      setExcludeDates(((config?.excludeInstallationDates ?? []) as string[]).map(str => parseISO(str)));
      if (shouldUseLookup) {
        installationTimeField.value = '';
        getTechnicianDates(
          dayAfterTomorrowDate,
          maxInstallationDate,
          tuiUnavailable ? undefined : tui,
          tuiUnavailable ? (installationAddressField?.value?.id ? undefined : customer?.houseNumber) : undefined,
          street,
          suburb,
          city,
          postCode,
          undefined,
          undefined,
          undefined,
          isDartMigration ? DartMigrationCallCode : undefined
        ).then(dates => {
          if (dates && dates.length > 0) {
            // setAvailableDates(dates.map((d) => d.date));
            const filteredDates = dates.filter(date => {
              const datesArr = date?.slots.map(d => d.code);
              return !(datesArr.length === 2 && datesArr.includes('AM1') && datesArr.includes('PM2'));
            });
            setAvailableDates(filteredDates.map(d => d.date));
          } else {
            installationDateField.setValue(new Date('9999-12-31'));
            installationTimeField.setValue('AM1');
            getAllDates();
          }
        });
      } else {
        getAllDates();
      }
    };

    const getAllDates = () => {
      setAvailableDates(undefined);
      const dayAfterTomorrowDate: Date = addDays(new Date(), 2);
      const maxInstallationDate: Date = addMonths(dayAfterTomorrowDate, 3);
      const excludeInstallationSundayDates: Date[] = eachWeekendOfInterval({
        start: dayAfterTomorrowDate,
        end: maxInstallationDate,
      }).filter(date => getDay(date) === 0);

      setExcludeDates(
        ((config?.excludeInstallationDates ?? []) as string[])
          .map(str => parseISO(str))
          .concat(excludeInstallationSundayDates)
      );
    };

    getDates();
  }, [propertyDetail.address, serviceAddress]);
  const placeHolder = isCorrectAddress ? currentAddress : 'Please start typing your address...';
  const addressText = isCorrectAddress ? (
    <>
      If your address has changed please call us on{' '}
      <a href={`tel:${telPhoneNumber}`} className="text-primary-bright-blue underline">
        {telPhoneNumber}
      </a>{' '}
      to update.
    </>
  ) : (
    <>
      Your address <span className="font-bold">{capitalize(currentAddress)}</span> appears to be invalid. Please
      re-enter your current address below:
    </>
  );
  return (
    <>
      <div className="px-4 py-6 md:px-0">
        <h1 className="sky-h3-reg md:sky-h4-reg">
          Sky TV <span className="sky-h3-black md:sky-h4-black">Installation:</span>
        </h1>
        <div className="sky-h7-reg">
          {`Our technician will need to visit your home to install your new Sky Box(es), cabling and if needed, a Sky satellite dish${
            customer ? '.' : ' - all '
          }`}
          {!customer && <span className="font-bold">free of charge.</span>}
        </div>
        {/* {currentAddress && (
          <p className="pt-4">
            We have your current address <span className="font-bold">{capitalize(currentAddress)}</span>, if your
            address has changed please call us on{' '}
            <a href={`tel:${telPhoneNumber}`} className="text-primary-bright-blue underline">
              {telPhoneNumber}
            </a>{' '}
            to update your address.
          </p>
        )} */}
      </div>
      {/* Latest designs does not have Covid 19 message - 19/04/2022 */}
      {/* <div>
        <p className="bg-gray-dark p-3 rounded m-2">
          Sky is obligated to ensure safe working conditions for our technicians and customers. All of our technicians
          are fully vaccinated and are required to wear masks while visiting customer homes to install or repair Sky
          services. <br />
          <br />
          If you, or anyone in your household, is awaiting the results of a COVID-19 test, are a confirmed COVID-19
          close contact or experiencing any COVID-19 symptoms, please contact our friendly NZ based team on 0800 64 64
          64 to discuss your installation.
        </p>
      </div> */}

      <div className="px-4 md:p-0 mb-2">
        <div className="flex flex-col gap-7 md:gap-8">
          {customer && (
            <div className="border-2 rounded-lg p-7 md:p-8 primary-mid-night-50">
              <h2 className="sky-h5-black md:sky-h6-black">Confirm Your Details</h2>
              <h3 className="sky-h7-reg">
                Please confirm your details so our technician can get in touch if they need.
              </h3>
              <div className="flex flex-col md:flex-row gap-7 md:gap-8 mt-4">
                <div className="flex-1 md:w-1/2">
                  <label className="block sky-label md:sky-label">Physical address</label>
                  <label className="block sky-h7-reg mb-2" style={{ color: '#464646' }}>
                    {addressText}
                  </label>
                  <ValidatedAddressSuggest
                    addressField={installationAddressField!}
                    formValidation={formValidation}
                    id="service-address"
                    className="w-full rounded-md"
                    placeholder={placeHolder}
                    addressType="Address"
                    disabled={isCorrectAddress}
                    addressService={addressService}
                    excludeExpression="^PO BOX"
                  />
                </div>
                <div className="flex-1 md:w-1/2">
                  <label className="block sky-label text-primary-mid-night-blue md:sky-label">Phone Number</label>
                  <label className="block sky-h7-reg mb-2" style={{ color: '#464646' }}>
                    Mobile preferred.
                  </label>
                  <ValidatedPhoneNumber
                    areaCodeField={areaCodeField!}
                    lineNumberField={phoneNumberField!}
                    onAreaCodeFieldChange={areaCodeField?.props.onChange!}
                    onLineNumberFieldChange={phoneNumberField?.props.onChange!}
                    id="account-details-installation-details-phone-number"
                  />
                </div>
              </div>
            </div>
          )}
          {loadingTechnicianAvailableDates ? (
            <div className="relative h-32">
              <Loader bgColor="sky-bg-transparent" bgOpacity={false} />
            </div>
          ) : (
            <div className="border-2 rounded-lg p-7 md:p-8 primary-mid-night-50">
              <h2 className="sky-h5-black md:sky-h6-black">Your Installation Details</h2>
              {technicianSlots.length > 0 ? (
                <>
                  <div className="flex flex-col md:flex-row gap-7 md:gap-8 mt-4 pb-2">
                    <div className="flex-1">
                      <label className="block sky-label md:sky-label mb-2">Ideal installation date</label>
                      <ValidationDatePicker
                        fieldValidation={installationDateField}
                        placeholderText="dd/mm/yyyy"
                        id="account-details-installation-details-installation-date"
                        formValidation={formValidation}
                        className="w-full rounded-md"
                        includeDates={availableDates}
                        excludeDates={excludeDates}
                        minDate={dayAfterTomorrowDate}
                        maxDate={maxInstallationDate}
                        disabled={!tui}
                      />
                      {!tui && <div className="sky-text-gray-darker">Ideal installation time</div>}
                    </div>
                    <div className="flex-1">
                      <label className="block sky-label md:sky-label mb-2">Ideal installation time</label>
                      <SelectElement
                        id="account-details-installation-details-installation-time"
                        className="w-full"
                        fieldValidation={installationTimeField}
                        formValidation={formValidation}
                        onChange={installationTimeField.props.onChange}
                        value={installationTimeField.value}
                        disabled={!tui}
                        data-testid="account-details-installation-details-installation-time">
                        <option value="" disabled>
                          Select an Option
                        </option>
                        {manipulatedSlots.map(slot => (
                          // {availableTimeSlots.map(slot => (
                          <option key={slot.code} value={slot.code}>
                            {slot.displayName}
                          </option>
                        ))}
                      </SelectElement>
                    </div>
                  </div>
                  <div>
                    <div className="flex flex-col md:flex-row gap-7 md:gap-8 mt-4 pb-2">
                      <div className="flex-1">
                        <label className="block sky-label md:sky-label mb-2">
                          Will there be any dog/s at home at the time of installation?
                        </label>
                        <RadioButtonGroup
                          formValidation={formValidation}
                          fieldValidation={dogsField}
                          options={sKyTvInstallationYesNoOptions.YESNO}
                          onSelect={dogsField?.props.onChange!}
                          selectedOption={dogsField?.value}
                          className="mt-0"
                          id="dogs"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block sky-label md:sky-label mb-2">
                          Will you be home at the time of installation?
                        </label>
                        <RadioButtonGroup
                          options={sKyTvInstallationYesNoOptions.YESNO}
                          onSelect={mainContactAvailabilityField?.props.onChange!}
                          selectedOption={mainContactAvailabilityField?.value}
                          className="mt-0"
                          id="account-holder"
                        />
                      </div>
                    </div>
                    {mainContactAvailabilityField?.value === 'No' ? (
                      <div className="flex flex-col md:flex-row gap-7 md:gap-8 mt-4">
                        <div className="flex-1">
                          <label className="block sky-label text-primary-mid-night-blue md:sky-label">
                            Alternative contact name
                          </label>
                          <label className="block mb-2 sky-h7-reg" style={{ color: '#464646' }}>
                            Someone over the age of 18 must be present for installation
                          </label>
                          <ValidatedTextInput
                            fieldValidation={alternateNameField!}
                            formValidation={formValidation}
                            id="account-details-installation-details-alternate-name"
                            className="w-full"
                            type="text"
                            onChange={str => {
                              alternateNameField?.props.onChange(capitalize(str));
                            }}
                            placeholder="John Smith"
                          />
                        </div>
                        <div className="flex-1 md:pt-4 pb-2">
                          <label className="block sky-label text-primary-mid-night-blue md:sky-label mb-2">
                            Alternative contact number
                          </label>
                          <ValidatedPhoneNumber
                            areaCodeField={alternateAreaCodeField!}
                            lineNumberField={alternatePhoneNumberField!}
                            onAreaCodeFieldChange={alternateAreaCodeField?.props.onChange!}
                            onLineNumberFieldChange={alternatePhoneNumberField?.props.onChange!}
                            id="account-details-installation-details-alternate-phone-number"
                          />
                        </div>
                      </div>
                    ) : null}
                    <div className="mt-4">
                      <label className="block sky-label text-primary-mid-night-blue md:sky-label mb-2">
                        Additional notes (optional)
                      </label>
                      <ValidatedTextArea
                        fieldValidation={techNotesField!}
                        formValidation={formValidation}
                        id="account-details-installation-details-additional-notes"
                        className="w-full min-h-40 md:min-h-24 resize-none"
                        type="text"
                        onChange={str => {
                          techNotesField?.props.onChange(firstLetterCapitalize(str));
                        }}
                        placeholder="Gate codes, parking instructions, access restrictions etc."
                        maxLength={105}
                      />
                    </div>
                  </div>
                </>
              ) : errorTechnicianAvailableDates &&
                (!customer || isCorrectAddress || installationAddressField?.value?.id) ? (
                <div className=" w-full mt-4 rounded-md border-l-6 border-blue-light bg-blue-cool inline-block px-6 py-4 sky-h6-reg">
                  One of our friendly crew will be in touch to schedule an installation for your new Sky device.
                </div>
              ) : (
                <></>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export { InstallationDetails };
